import React from 'react';

import i18n from 'i18next';
import { Button, CardRow, Icon, Numbers, ButtonDropdown, DropdownItem, Dropdown, InfoText, Loader } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Category from '../../../models/category';
import Inventory from '../../../models/inventory';
import Zone from '../../../models/zoneModel';
import Contingent from '../../../models/contingentModel';
import Style from './chartList.module.css';
import PlaceholderImage from '../../../images/placeholder-image.png';

function ChartImage({ chart }) {
    return (
        <div
            className={Style['chart-card-banner-container']}
            style={{
                backgroundImage: `url(${chart?.preview_picture?.url || PlaceholderImage})`
            }}
        />
    );
}

function ChartName({ chart }) {
    return (
        <div className={Style['chart-card-title']}>
            <div>{chart.name}</div>
        </div>
    );
}

function ChartInfoText({ total, subtitle, loading }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    return (
        <InfoText className={Style['info-text']} subtitle={t(`${subtitle}`, { count: total || 0 })}>
            <div className={Style['total']}>{loading ? <Loader inline size="small" /> : <Numbers value={total || 0} locale={locale} />}</div>
        </InfoText>
    );
}

export default function ChartCard({ organization, chart, setChartInDrawer, setShowDrawer, onChartDeleted, onChartDuplicated }) {
    const { t } = useTranslation();

    const [categories, loadingCategories] = Category.useApiModel({
        organizationId: organization.id,
        chartId: chart.uuid
    });
    const [inventories, loadingInventories] = Inventory.useApiModel({
        organizationId: organization.id,
        chartId: chart.uuid
    });
    const [zones, loadingZones] = Zone.useApiModel({
        organizationId: organization.id,
        chartId: chart.uuid
    });
    const [contingents, loadingContingents] = Contingent.useApiModel({
        organizationId: organization.id,
        chartId: chart.uuid
    });

    const handleDuplicate = React.useCallback(() => {
        onChartDuplicated(chart);
    }, [chart, onChartDuplicated]);

    return (
        <CardRow className={Style['chart-card-content']} as="a" href={`/ticket/O${organization.id}/charts/${chart.pk}/dashboard`}>
            <CardRow.LeftComponent>
                <div className={Style['chart-card-infos']}>
                    <ChartImage chart={chart} />
                </div>
            </CardRow.LeftComponent>
            <CardRow.RightComponent>
                <>
                    <div>
                        <ChartName chart={chart} />
                    </div>
                    <div className={Style['chart-row-container']}>
                        <div className={Style['chart-card-text-infos']}>
                            <ChartInfoText total={categories?.length} loading={loadingCategories} subtitle="ticket.charts.chartlist.categories" />
                            <ChartInfoText total={inventories?.length} loading={loadingInventories} subtitle="ticket.charts.chartlist.inventories" />
                            <ChartInfoText total={zones?.length} loading={loadingZones} subtitle="ticket.charts.chartlist.zones" />
                            <ChartInfoText total={contingents?.length} loading={loadingContingents} subtitle="ticket.charts.chartlist.contingents" />
                            <ButtonDropdown
                                onClick={e => e.preventDefault()}
                                trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                                items={[
                                    <div key="items" onClick={e => e.preventDefault()}>
                                        <DropdownItem
                                            key="settings"
                                            item={t('ticket.charts.chartlist.settings')}
                                            onClick={() => {
                                                setChartInDrawer(chart);
                                                setShowDrawer(true);
                                            }}
                                        />
                                        {onChartDuplicated ? <DropdownItem key="duplicate" item={t('ticket.common.action.duplicate')} onClick={handleDuplicate} /> : null}
                                        <Dropdown.Line />
                                        <DropdownItem
                                            disabled={!!inventories?.length}
                                            deleteItem
                                            key="delete"
                                            item={t('ticket.common.action.delete')}
                                            onClick={() => {
                                                onChartDeleted(chart);
                                            }}
                                        />
                                    </div>
                                ]}
                            />
                        </div>
                    </div>
                </>
            </CardRow.RightComponent>
        </CardRow>
    );
}
